import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import LicenseMasterServices from "../services/API/LicenseMaster";

const POLL_INTERVAL_MS = 150000; // 5 minutes

// Drives a queue-based export: kicks off an export job, polls its status,
// and persists progress in localStorage so the "Download Ready" button
// survives a page refresh or logout/login.
const useAsyncExport = (storageKey, type, defaultFileName) => {
  const [status, setStatus] = useState(null); // null | 'pending' | 'processing' | 'completed'
  const [fileName, setFileName] = useState(defaultFileName);
  const jobIdRef = useRef(null);
  const lockRef = useRef(false);
  const pollTimerRef = useRef(null);

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const persist = (jobId, jobStatus) => {
    if (jobId) {
      localStorage.setItem(storageKey, JSON.stringify({ jobId, status: jobStatus }));
    } else {
      localStorage.removeItem(storageKey);
    }
  };

  const schedulePoll = useCallback(
    (jobId) => {
      stopPolling();
      pollTimerRef.current = setTimeout(async () => {
        const resp = await LicenseMasterServices.getExportJobStatus(jobId);
        const job = resp?.data;

        if (!job) {
          schedulePoll(jobId);
          return;
        }

        if (job.status === "completed") {
          setStatus("completed");
          setFileName(job.file_name || defaultFileName);
          persist(jobId, "completed");
        } else if (job.status === "failed") {
          toast.error(job.error || "Export failed. Please try again.");
          lockRef.current = false;
          jobIdRef.current = null;
          setStatus(null);
          persist(null, null);
        } else {
          setStatus(job.status);
          persist(jobId, job.status);
          schedulePoll(jobId);
        }
      }, POLL_INTERVAL_MS);
    },
    [defaultFileName, storageKey]
  );

  // Restore a pending/completed job on mount so it survives refresh/logout-login.
  useEffect(() => {
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      try {
        const { jobId, status: savedStatus } = JSON.parse(saved);
        if (jobId) {
          jobIdRef.current = jobId;
          lockRef.current = true;
          if (savedStatus === "completed") {
            setStatus("completed");
          } else {
            setStatus(savedStatus || "pending");
            schedulePoll(jobId);
          }
        }
      } catch (e) {
        localStorage.removeItem(storageKey);
      }
    }
    return stopPolling;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startExport = async (extraParams = {}) => {
    if (lockRef.current) {
      toast.info("Export already in progress");
      return;
    }
    lockRef.current = true;
    setStatus("pending");
    toast.info("Export started. It will be ready for download shortly.");

    const resp = await LicenseMasterServices.exportAsync({ type, ...extraParams });
    const jobId = resp?.data?.export_job_id;
    if (!jobId) {
      lockRef.current = false;
      setStatus(null);
      return;
    }

    jobIdRef.current = jobId;
    persist(jobId, "pending");
    schedulePoll(jobId);
  };

  const downloadReady = async () => {
    const jobId = jobIdRef.current;
    if (!jobId) return;

    const resp = await LicenseMasterServices.downloadExportJob(jobId);
    if (resp) {
      const url = window.URL.createObjectURL(new Blob([resp]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName || defaultFileName);
      link.click();
      await LicenseMasterServices.deleteExportJob(jobId);
    }

    stopPolling();
    persist(null, null);
    jobIdRef.current = null;
    lockRef.current = false;
    setStatus(null);
    setFileName(defaultFileName);
  };

  return {
    isExporting: status === "pending" || status === "processing",
    isReady: status === "completed",
    startExport,
    downloadReady,
  };
};

export default useAsyncExport;
